import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../components/AppShell";
import PageHero from "../components/PageHero";
import { api, formatError, API, BACKEND_URL } from "../lib/api";
import { toast } from "sonner";
import {
  FolderOpen, FolderPlus, Folder, Plus, Trash2, Star, FileText,
  CalendarDays, ExternalLink, LayoutGrid, List as ListIcon,
  ChevronLeft, ChevronRight, Table as TableIcon, MoveRight,
  Sparkles, Award, TrendingUp,
} from "lucide-react";
import { Link } from "react-router-dom";
import ProjectPreviewModal from "../components/ProjectPreviewModal";

// Human-readable label for each financial-tool slug. Kept in one place so
// list/card views + preview modal all render the same brand name.
const FINANCIAL_TOOL_NAMES = {
  "sales-estimator": "Financial Projection",
  "value-chain":     "Value Chain Calculator",
  "retail-margin":   "Retail Margin Calculator",
  "cap-table":       "Cap Table Modeler",
};
function financialToolLabel(kind) {
  return FINANCIAL_TOOL_NAMES[kind] || "Financial Projection";
}

// Projects workspace — supports folders + loose items, with a list/card
// view toggle and per-item move-to-folder. Everything a founder has saved
// (worksheet projects + database saved-lists) shows here.
export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [lists, setLists] = useState([]);
  const [stars, setStars] = useState([]);
  const [dbNames, setDbNames] = useState({});
  // Live blueprint state — used to decorate Launch Blueprint items in
  // this page: draft "Open" is gated while a submission is under
  // review, and submitted cards surface the current score if the
  // panel has already written one back.
  const [blueprintLive, setBlueprintLive] = useState(null);
  const [view, setView] = useState(() => localStorage.getItem("projectsView") || "cards");
  const [currentFolderId, setCurrentFolderId] = useState(null);   // null = root
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [newProjectName, setNewProjectName] = useState("");
  const [movingItem, setMovingItem] = useState(null); // {kind: "project"|"list", id, currentParent}
  // Bulk selection: set of "kind:id" strings so we can distinguish
  // projects from saved-lists sharing the same numeric id space.
  const [selected, setSelected] = useState(() => new Set());
  const [bulkMoving, setBulkMoving] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);

  useEffect(() => {
    localStorage.setItem("projectsView", view);
  }, [view]);

  const load = () => {
    api.get("/projects").then((r) => setProjects(r.data || [])).catch(() => {});
    api.get("/lists").then((r) => setLists(r.data || [])).catch(() => {});
    api.get("/stars").then((r) => setStars(r.data || [])).catch(() => {});
    api.get("/databases").then((r) =>
      setDbNames(Object.fromEntries(r.data.map((d) => [d.slug, d.name])))
    ).catch(() => {});
    api.get("/blueprint/me").then((r) => setBlueprintLive(r.data || null)).catch(() => setBlueprintLive(null));
  };
  useEffect(() => { load(); }, []);

  // Split into folders vs items
  const folders = useMemo(
    () => projects.filter((p) => p.is_folder),
    [projects],
  );
  const items = useMemo(
    () => projects.filter((p) => !p.is_folder),
    [projects],
  );

  // Count everything inside a folder (projects + lists + folder-attached
  // agreement intakes). Agreement intakes attached directly to a folder
  // via the Save-to-Project picker live in that folder's own
  // `agreements_intakes` array, so we surface them in the folder count
  // too — otherwise a folder holding only intakes reads as empty.
  const folderCounts = useMemo(() => {
    const counts = {};
    for (const f of folders) counts[f.id] = 0;
    for (const it of items) if (it.parent_id) counts[it.parent_id] = (counts[it.parent_id] || 0) + 1;
    for (const l of lists) if (l.project_id) counts[l.project_id] = (counts[l.project_id] || 0) + 1;
    for (const f of folders) {
      if (Array.isArray(f.agreements_intakes) && f.agreements_intakes.length > 0) {
        counts[f.id] = (counts[f.id] || 0) + f.agreements_intakes.length;
      }
    }
    return counts;
  }, [folders, items, lists]);

  const currentFolder = currentFolderId ? folders.find((f) => f.id === currentFolderId) : null;

  // Items shown in the current view
  const visibleItems = useMemo(() => {
    const wantParent = currentFolderId; // null when at root
    // Exclude wrapper projects — these are just holders for a saved
    // CPG Agreements plan and are surfaced through the intake card
    // itself (see below). Otherwise a save would show TWO cards for
    // the same submission (the empty wrapper + the intake).
    const projItems = items.filter(
      (it) => (it.parent_id || null) === wantParent && it.type !== "agreements_wrapper",
    );
    const listItems = lists.filter((l) => (l.project_id || null) === wantParent).map((l) => ({
      ...l, _kind: "list",
    }));
    // CPG Agreements draft wrappers — a wrapper project that stores
    // a filled template via `agreements_draft_snapshot` (not a saved
    // intake-plan). Surfaced as its own card so founders can reopen
    // the template with the saved blanks pre-filled and keep editing.
    const draftItems = [];
    for (const p of projects) {
      if (p.type !== "agreements_wrapper") continue;
      if (!p.agreements_draft_snapshot || !p.agreements_doc_id) continue;
      // Skip plan wrappers (they already surface via agreements_intakes).
      if (Array.isArray(p.agreements_intakes) && p.agreements_intakes.length > 0) continue;
      const showAtParent = p.parent_id || null;
      if (showAtParent !== wantParent) continue;
      draftItems.push({
        _kind: "agreements-draft",
        id: p.id,
        name: p.name,
        agreements_doc_id: p.agreements_doc_id,
        agreements_template_id: p.agreements_template_id,
        agreements_saved_at: p.agreements_saved_at,
        agreements_draft_snapshot: p.agreements_draft_snapshot,
        parent_id: showAtParent,
        created_at: p.created_at,
      });
    }
    // Explode every project's `agreements_intakes` array into its own
    // sibling item so multiple intake submissions saved to the same
    // project/folder show individually.
    //
    // Two owning kinds must be handled:
    //   1. Folder owns intakes  → intake shows INSIDE that folder,
    //      i.e., visible when the founder has opened that folder
    //      (wantParent === folder.id).
    //   2. Non-folder project (including agreements_wrapper) owns
    //      intakes → intake shows ALONGSIDE its owning project in
    //      whichever folder holds the project
    //      (wantParent === project.parent_id).
    //
    // Without case (1) an intake attached to a folder would silently
    // vanish from the UI — the exact bug the founder reported.
    const agreementsItems = [];
    for (const p of projects) {
      if (!Array.isArray(p.agreements_intakes) || p.agreements_intakes.length === 0) continue;
      const owningIsFolder = !!p.is_folder;
      const showAtParent = owningIsFolder ? p.id : (p.parent_id || null);
      if (showAtParent !== wantParent) continue;
      for (const a of p.agreements_intakes) {
        if (!a?.intakeProjectId) continue;
        agreementsItems.push({
          _kind: "agreements-intake",
          _owningProjectId: p.id,
          _owningIsFolder: owningIsFolder,
          _owningIsWrapper: p.type === "agreements_wrapper",
          _owningProjectName: p.name || p.title || "",
          id: `agr-${p.id}-${a.intakeProjectId}`,
          intakeProjectId: a.intakeProjectId,
          // Prefer the wrapper project's own name (it captures the
          // display name the founder typed at save time). Fall back to
          // the intake's cached brand name for legacy shared-bucket
          // records that predate the wrapper model.
          brand_name: (p.type === "agreements_wrapper" ? (p.name || a.brandName) : a.brandName) || "Untitled brand",
          attached_at: a.attachedAt || p.created_at,
          created_at: a.attachedAt || p.created_at,
          counts: a.counts,
          parent_id: showAtParent,
        });
      }
    }
    return [
      ...projItems.map((p) => ({ ...p, _kind: "project" })),
      ...listItems,
      ...agreementsItems,
      ...draftItems,
    ];
  }, [projects, items, lists, currentFolderId]);

  const createFolder = async (e) => {
    e.preventDefault();
    const name = newFolderName.trim();
    if (!name) return;
    try {
      await api.post("/projects", {
        name,
        is_folder: true,
        parent_id: currentFolderId || null,
      });
      setNewFolderName(""); setCreatingFolder(false); load();
      toast.success("Folder created");
    } catch (e) { toast.error(formatError(e)); }
  };

  const createLooseProject = async (e) => {
    e.preventDefault();
    const name = newProjectName.trim();
    if (!name) return;
    try {
      await api.post("/projects", { name, parent_id: currentFolderId || null });
      setNewProjectName(""); load();
      toast.success("Project created");
    } catch (e) { toast.error(formatError(e)); }
  };

  const removeProject = async (id) => {
    if (!window.confirm("Delete this? Anything inside will be moved to the root.")) return;
    // Optimistic: strip from local state immediately so the row/card
    // disappears without waiting on the server round-trip. Prevents
    // the "Already deleted — refreshing" ghost where a stale render
    // sat on screen because `load()` fired after the toast and
    // resurrected the just-deleted record before the DELETE fully
    // propagated. Since the delete goal is satisfied either way
    // (server had it → gone; server didn't → nothing to do), we
    // trust the optimistic strip and only re-sync on unexpected
    // errors.
    setProjects((prev) => prev.filter((p) => p.id !== id));
    try {
      await api.delete(`/projects/${id}`);
    } catch (e) {
      if (e?.response?.status === 404) {
        toast.success("Already deleted.");
        return;
      }
      toast.error(formatError(e));
      // Unknown failure: reconverge with the server so we don't
      // lie to the founder about the current state.
      load();
    }
  };
  const removeList = async (id) => {
    if (!window.confirm("Delete this saved list?")) return;
    setLists((prev) => prev.filter((l) => l.id !== id));
    try { await api.delete(`/lists/${id}`); }
    catch (e) {
      if (e?.response?.status === 404) return;
      toast.error(formatError(e));
      load();
    }
  };
  // Detach a single agreement intake from its owning folder/project's
  // `agreements_intakes` array. When the owner is an `agreements_wrapper`
  // project (holds exactly one intake by construction), also delete the
  // wrapper itself so the founder does not end up with an empty phantom
  // card. Optimistic-update the local projects state so the card
  // disappears immediately — the reload keeps us truthful with the
  // server. Prevents the "Already deleted, still showing AT3, AT2"
  // ghost-item bug the founder reported.
  const removeAgreementIntake = async (item) => {
    if (!window.confirm(`Delete "${item.brand_name}"?`)) return;
    const owningId = item._owningProjectId;
    const intakeId = item.intakeProjectId;
    const isWrapper = !!item._owningIsWrapper;
    setProjects((prev) => {
      if (isWrapper) return prev.filter((p) => p.id !== owningId);
      return prev.map((p) => p.id === owningId
        ? { ...p, agreements_intakes: (p.agreements_intakes || []).filter((a) => a.intakeProjectId !== intakeId) }
        : p,
      );
    });
    try {
      await api.delete(`/agreements/plan/${encodeURIComponent(intakeId)}/detach-from/${encodeURIComponent(owningId)}`);
      if (isWrapper) {
        // Best-effort — a 404 here is fine (the wrapper is gone either way).
        try { await api.delete(`/projects/${owningId}`); } catch (_) {}
      }
      toast.success("Deleted.");
    } catch (e) {
      toast.error(formatError(e));
      load();
    }
  };
  const unstar = async (s) => {
    try { await api.delete("/stars", { data: { database_slug: s.database_slug, row_id: s.row_id } }); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const moveItem = async (item, folderId) => {
    try {
      if (item._kind === "list") {
        await api.put(`/lists/${item.id}`, { project_id: folderId || "" });
      } else {
        await api.patch(`/projects/${item.id}`, { parent_id: folderId || "" });
      }
      toast.success(folderId ? "Moved to folder" : "Moved to root");
      setMovingItem(null); load();
    } catch (e) { toast.error(formatError(e)); }
  };

  // ── Bulk selection helpers ─────────────────────────────────────────────
  const toggleSelected = (item) => {
    const key = `${item._kind}:${item.id}`;
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const isSelected = (item) => selected.has(`${item._kind}:${item.id}`);
  const clearSelection = () => setSelected(new Set());
  const selectAllVisible = () => {
    setSelected(new Set(visibleItems.map(it => `${it._kind}:${it.id}`)));
  };
  // Reset selection whenever the user navigates between folders.
  useEffect(() => { clearSelection(); }, [currentFolderId]);

  const bulkMoveToFolder = async (folderId) => {
    if (selected.size === 0) return;
    const byKey = new Map(visibleItems.map(it => [`${it._kind}:${it.id}`, it]));
    const targets = Array.from(selected)
      .map(key => byKey.get(key))
      .filter(Boolean);
    try {
      await Promise.all(targets.map(item => {
        if (item._kind === "list") {
          return api.put(`/lists/${item.id}`, { project_id: folderId || "" });
        }
        // Agreements-intake cards move their WRAPPER project so the whole
        // CPG Agreements card travels together to the new folder.
        const targetId = item._kind === "agreements-intake" ? item._owningProjectId : item.id;
        return api.patch(`/projects/${targetId}`, { parent_id: folderId || "" });
      }));
      toast.success(
        folderId
          ? `Moved ${targets.length} item${targets.length === 1 ? "" : "s"} to folder`
          : `Moved ${targets.length} item${targets.length === 1 ? "" : "s"} to root`,
      );
      clearSelection();
      setBulkMoving(false);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  // Bulk delete — deletes every selected item using the same rules as
  // the per-row Delete button. Optimistically clears state first so the
  // grid/list empties instantly; the reload keeps us truthful on error.
  const bulkDelete = async () => {
    if (selected.size === 0) return;
    const byKey = new Map(visibleItems.map(it => [`${it._kind}:${it.id}`, it]));
    const targets = Array.from(selected)
      .map(key => byKey.get(key))
      .filter(Boolean);
    if (targets.length === 0) return;
    if (!window.confirm(`Delete ${targets.length} item${targets.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
    const projectIdsToDrop = new Set();
    const wrapperIdsToDrop = new Set();
    const listIdsToDrop = new Set();
    for (const it of targets) {
      if (it._kind === "list") listIdsToDrop.add(it.id);
      else if (it._kind === "project") projectIdsToDrop.add(it.id);
      else if (it._kind === "agreements-intake" && it._owningIsWrapper) wrapperIdsToDrop.add(it._owningProjectId);
    }
    // Optimistic strip.
    if (projectIdsToDrop.size || wrapperIdsToDrop.size) {
      setProjects((prev) => prev.filter((p) => !projectIdsToDrop.has(p.id) && !wrapperIdsToDrop.has(p.id)));
    }
    if (listIdsToDrop.size) {
      setLists((prev) => prev.filter((l) => !listIdsToDrop.has(l.id)));
    }
    try {
      await Promise.all(targets.map(async (it) => {
        if (it._kind === "list") {
          try { await api.delete(`/lists/${it.id}`); } catch (e) { if (e?.response?.status !== 404) throw e; }
        } else if (it._kind === "project") {
          try { await api.delete(`/projects/${it.id}`); } catch (e) { if (e?.response?.status !== 404) throw e; }
        } else if (it._kind === "agreements-intake") {
          try { await api.delete(`/agreements/plan/${encodeURIComponent(it.intakeProjectId)}/detach-from/${encodeURIComponent(it._owningProjectId)}`); } catch (_) {}
          if (it._owningIsWrapper) {
            try { await api.delete(`/projects/${it._owningProjectId}`); } catch (_) {}
          }
        }
      }));
      toast.success(`Deleted ${targets.length} item${targets.length === 1 ? "" : "s"}.`);
      clearSelection();
    } catch (e) {
      toast.error(formatError(e));
      load();
    }
  };

  const starsByDb = stars.reduce((acc, s) => {
    (acc[s.database_slug] ||= []).push(s); return acc;
  }, {});

  return (
    <AppShell title="My Projects">
      <div className="w-full">
        <PageHero
          eyebrow="Your workspace"
          icon={Folder}
          title={<>My <span className="text-grad">Projects</span></>}
          subtitle="Group worksheets, saved lists, and tool outputs into folders you can revisit anytime."
        />

        {/* Breadcrumb + view toggle */}
        <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
          <nav className="flex items-center gap-2 text-sm">
            <button
              onClick={() => setCurrentFolderId(null)}
              data-testid="projects-crumb-root"
              className={"font-semibold " + (currentFolderId ? "text-primary hover:underline" : "text-foreground")}
            >
              All projects
            </button>
            {currentFolder && (
              <>
                <ChevronRight className="w-3 h-3 text-muted-foreground" />
                <span className="font-semibold" data-testid="projects-crumb-current">{currentFolder.name}</span>
              </>
            )}
          </nav>
          <div className="inline-flex items-center gap-1 border border-shell-border rounded-full p-1 bg-white" data-testid="projects-view-toggle">
            <button
              onClick={() => setView("cards")}
              data-testid="projects-view-cards"
              className={"inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full transition-colors " +
                (view === "cards" ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground")}
            >
              <LayoutGrid className="w-3.5 h-3.5" /> Cards
            </button>
            <button
              onClick={() => setView("list")}
              data-testid="projects-view-list"
              className={"inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full transition-colors " +
                (view === "list" ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground")}
            >
              <ListIcon className="w-3.5 h-3.5" /> List
            </button>
          </div>
        </div>

        {/* Actions row */}
        <div className="mt-6 flex flex-col sm:flex-row sm:flex-wrap sm:items-start gap-3">
          <form onSubmit={createLooseProject} className="flex flex-col sm:flex-row gap-2 w-full sm:flex-1 sm:min-w-[280px]">
            <input
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              placeholder={currentFolder
                ? `Add a project in "${currentFolder.name}"`
                : "New project (e.g. Kettle Chips 2026 Launch)"}
              data-testid="project-name"
              className="w-full sm:flex-1 rounded-md border border-shell-border bg-white px-3 py-2 text-sm"
            />
            <button className="btn-amber whitespace-nowrap justify-center" data-testid="project-add">
              Create New Project
            </button>
          </form>
          {creatingFolder ? (
            <form onSubmit={createFolder} className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto" data-testid="projects-new-folder-form">
              <input
                autoFocus
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder={currentFolder
                  ? `Subfolder in "${currentFolder.name}"`
                  : "Folder name"}
                data-testid="project-folder-name"
                onKeyDown={(e) => { if (e.key === "Escape") { setCreatingFolder(false); setNewFolderName(""); } }}
                className="w-full sm:w-auto rounded-md border border-shell-border bg-white px-3 py-2 text-sm"
              />
              <div className="flex gap-2">
                <button className="btn-primary whitespace-nowrap flex-1 sm:flex-none justify-center" data-testid="project-folder-create">
                  <FolderPlus className="w-4 h-4" /> Create
                </button>
                <button
                  type="button"
                  onClick={() => { setCreatingFolder(false); setNewFolderName(""); }}
                  className="text-xs text-muted-foreground hover:text-foreground px-2 whitespace-nowrap"
                  data-testid="project-folder-cancel"
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <button
              onClick={() => setCreatingFolder(true)}
              data-testid="project-new-folder-btn"
              className="btn-outline inline-flex items-center justify-center gap-1 whitespace-nowrap w-full sm:w-auto"
            >
              <FolderPlus className="w-4 h-4" /> New folder
            </button>
          )}
        </div>

        {/* Folders section — only at root */}
        {currentFolderId === null && folders.length > 0 && (
          <section className="mt-10">
            <h2 className="font-display text-2xl flex items-center gap-2">
              <Folder className="w-5 h-5" /> Folders
              <span className="text-xs text-muted-foreground font-normal">· {folders.length}</span>
            </h2>
            {view === "cards" ? (
              <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="projects-folders-grid">
                {folders.map((f) => (
                  <FolderCard
                    key={f.id}
                    folder={f}
                    count={folderCounts[f.id] || 0}
                    onOpen={() => setCurrentFolderId(f.id)}
                    onDelete={() => removeProject(f.id)}
                  />
                ))}
              </div>
            ) : (
              <div className="mt-4 rounded-xl border border-shell-border bg-white overflow-x-auto" data-testid="projects-folders-list">
                <table className="w-full text-sm min-w-[520px]">
                  <thead className="bg-muted/60 text-xs uppercase tracking-widest text-muted-foreground">
                    <tr>
                      <th className="text-left px-4 py-2 font-semibold min-w-[220px]">Folder</th>
                      <th className="text-left px-4 py-2 font-semibold whitespace-nowrap">Items</th>
                      <th className="text-left px-4 py-2 font-semibold whitespace-nowrap">Created</th>
                      <th className="px-4 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {folders.map((f) => (
                      <tr key={f.id} className="border-t border-shell-border hover:bg-muted/30"
                          data-testid={`projects-folder-row-${f.id}`}>
                        <td className="px-4 py-3">
                          <button onClick={() => setCurrentFolderId(f.id)}
                                  className="inline-flex items-start gap-2 font-semibold text-foreground hover:text-primary text-left min-w-0 w-full">
                            <Folder className="w-4 h-4 text-shell-accent shrink-0 mt-0.5" />
                            <span className="break-words min-w-0">{f.name}</span>
                          </button>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{folderCounts[f.id] || 0}</td>
                        <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{new Date(f.created_at).toLocaleDateString()}</td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <button
                            onClick={() => removeProject(f.id)}
                            data-testid={`project-delete-${f.id}`}
                            className="text-muted-foreground hover:text-destructive text-xs inline-flex items-center gap-1"
                          >
                            <Trash2 className="w-3 h-3" /> Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {/* Items section (of current folder OR root loose items) */}
        <section className="mt-10">
          <h2 className="font-display text-2xl flex items-center gap-2">
            <FolderOpen className="w-5 h-5" />
            {currentFolder ? `Items in "${currentFolder.name}"` : "Projects"}
            <span className="text-xs text-muted-foreground font-normal">· {visibleItems.length}</span>
          </h2>
          {visibleItems.length === 0 ? (
            <div className="mt-4 surface p-10 text-center">
              <p className="font-display text-2xl">Nothing here yet</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {currentFolder
                  ? "Save a worksheet or database list into this folder to see it here."
                  : "Create a project, save a module worksheet, or save a database list to get started."}
              </p>
            </div>
          ) : view === "cards" ? (
            <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="projects-items-grid">
              {visibleItems.map((it) => (
                <ItemCard
                  key={`${it._kind}-${it.id}`}
                  item={it}
                  dbNames={dbNames}
                  blueprintLive={blueprintLive}
                  selected={isSelected(it)}
                  onToggleSelect={() => toggleSelected(it)}
                  onPreview={() => setPreviewItem(it)}
                  onDelete={() => (it._kind === "list" ? removeList(it.id) : it._kind === "project" ? removeProject(it.id) : it._kind === "agreements-intake" ? removeAgreementIntake(it) : it._kind === "agreements-draft" ? removeProject(it.id) : null)}
                  onMove={() => setMovingItem(
                    it._kind === "agreements-intake"
                      // Move the WRAPPER project (kind='project' id=wrapperId)
                      // so the existing PATCH /projects/{id} flow can carry
                      // the CPG Agreements card into the picked folder as
                      // a single unit.
                      ? { kind: "project", id: it._owningProjectId, currentParent: it.parent_id, name: it.brand_name }
                      : { kind: it._kind, id: it.id, currentParent: it._kind === "list" ? it.project_id : it.parent_id, name: it.name },
                  )}
                />
              ))}
            </div>
          ) : (
            <ItemsListView
              items={visibleItems}
              dbNames={dbNames}
              blueprintLive={blueprintLive}
              onPreview={(it) => setPreviewItem(it)}
              selected={selected}
              onToggleSelect={toggleSelected}
              onDelete={(it) => (it._kind === "list" ? removeList(it.id) : it._kind === "project" ? removeProject(it.id) : it._kind === "agreements-intake" ? removeAgreementIntake(it) : it._kind === "agreements-draft" ? removeProject(it.id) : null)}
              onMove={(it) => setMovingItem(
                it._kind === "agreements-intake"
                  ? { kind: "project", id: it._owningProjectId, currentParent: it.parent_id, name: it.brand_name }
                  : { kind: it._kind, id: it.id, currentParent: it._kind === "list" ? it.project_id : it.parent_id, name: it.name },
              )}
            />
          )}
        </section>

        {/* Starred rows — root only */}
        {currentFolderId === null && (
          <section className="mt-12">
            <h2 className="font-display text-2xl flex items-center gap-2">
              <Star className="w-5 h-5 text-shell-accent" /> Starred records
            </h2>
            {stars.length === 0 ? (
              <div className="mt-4 surface p-10 text-center">
                <p className="font-display text-2xl">Nothing starred yet</p>
                <p className="mt-1 text-sm text-muted-foreground">Star rows inside any database to build a personal shortlist.</p>
              </div>
            ) : (
              <div className="mt-4 grid gap-4">
                {Object.entries(starsByDb).map(([slug, list]) => (
                  <div key={slug} className="surface p-5" data-testid={`starred-group-${slug}`}>
                    <div className="flex items-center justify-between">
                      <p className="font-semibold">
                        {dbNames[slug] || slug}
                        <span className="text-muted-foreground text-xs">· {list.length} starred</span>
                      </p>
                      {/* Group-level link: land on the Data tab, but do NOT
                          auto-highlight — that's reserved for clicking a
                          specific starred row below. */}
                      <Link
                        to={`/databases/${slug}?tab=data`}
                        className="text-xs font-semibold text-shell-side"
                        data-testid={`starred-open-db-${slug}`}
                      >
                        Open database →
                      </Link>
                    </div>
                    <ul className="mt-3 grid sm:grid-cols-2 gap-2">
                      {list.map((s) => (
                        <li key={s.row_id} data-testid={`starred-row-${s.row_id}`}
                          className="flex items-center justify-between gap-3 text-sm border border-shell-border rounded-md px-3 py-2 hover:bg-muted/40 transition-colors">
                          {/* Deep-link to the source database with the starred
                              row highlighted. Falls back to the persisted
                              display name (server-side row.name) with a
                              graceful "Row <id>" default when the row was
                              deleted upstream. */}
                          <Link
                            to={`/databases/${slug}?highlight=${encodeURIComponent(s.row_id)}`}
                            className="truncate flex-1 hover:text-shell-side"
                            data-testid={`starred-row-open-${s.row_id}`}
                            title={s.row_name || s.row_id}
                          >
                            {s.row_name || `Row ${s.row_id.slice(0, 8)}`}
                            {s.row_missing ? (
                              <span className="ml-2 text-[10px] uppercase tracking-widest text-muted-foreground">
                                deleted upstream
                              </span>
                            ) : null}
                          </Link>
                          <button onClick={() => unstar(s)} data-testid={`starred-unstar-${s.row_id}`}
                            className="text-muted-foreground hover:text-destructive shrink-0"
                            aria-label="Unstar">
                            <Star className="w-4 h-4 fill-shell-accent text-shell-accent" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
      </div>

      {movingItem && (
        <MoveToFolderModal
          movingItem={movingItem}
          folders={folders}
          onClose={() => setMovingItem(null)}
          onSelect={(folderId) => {
            const target = visibleItems.find((i) => i.id === movingItem.id) || items.find((i) => i.id === movingItem.id) || lists.find((l) => l.id === movingItem.id);
            if (target) moveItem({ ...target, _kind: movingItem.kind }, folderId);
          }}
        />
      )}

      {previewItem && (
        <ProjectPreviewModal
          item={previewItem}
          dbNames={dbNames}
          onClose={() => setPreviewItem(null)}
        />
      )}

      {selected.size > 0 && (
        <div
          data-testid="projects-bulk-bar"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-white shadow-2xl border border-shell-border rounded-full px-4 py-3 flex items-center gap-3 max-w-full"
        >
          <span className="text-sm font-semibold">
            {selected.size} selected
          </span>
          <span className="text-xs text-muted-foreground">·</span>
          <button
            type="button"
            onClick={selectAllVisible}
            data-testid="projects-bulk-select-all"
            className="text-xs font-semibold text-muted-foreground hover:text-foreground"
          >
            Select all visible
          </button>
          <button
            type="button"
            onClick={clearSelection}
            data-testid="projects-bulk-clear"
            className="text-xs font-semibold text-muted-foreground hover:text-foreground"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={() => setBulkMoving(true)}
            data-testid="projects-bulk-move"
            className="btn-primary text-xs inline-flex items-center gap-1"
          >
            <FolderOpen className="w-3.5 h-3.5" /> Move to folder
          </button>
          <button
            type="button"
            onClick={bulkDelete}
            data-testid="projects-bulk-delete"
            className="text-xs inline-flex items-center gap-1 font-semibold px-3 py-1.5 rounded-full text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete selected
          </button>
        </div>
      )}

      {bulkMoving && (
        <MoveToFolderModal
          movingItem={{ name: `${selected.size} items` }}
          folders={folders}
          bulk
          onClose={() => setBulkMoving(false)}
          onSelect={bulkMoveToFolder}
        />
      )}
    </AppShell>
  );
}

// ── Folder card ─────────────────────────────────────────────────────────
function FolderCard({ folder, count, onOpen, onDelete }) {
  return (
    <div className="surface p-6 flex flex-col group min-w-0" data-testid={`folder-card-${folder.id}`}>
      <button onClick={onOpen} className="flex items-start gap-2 text-left min-w-0">
        <Folder className="w-6 h-6 text-shell-accent shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="font-display text-xl break-words line-clamp-2">{folder.name}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{count} item{count === 1 ? "" : "s"}</p>
        </div>
      </button>
      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <button onClick={onOpen} className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1 whitespace-nowrap"
                data-testid={`folder-open-${folder.id}`}>
          Open <ChevronRight className="w-3 h-3" />
        </button>
        <button onClick={onDelete} data-testid={`project-delete-${folder.id}`}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive whitespace-nowrap">
          <Trash2 className="w-3 h-3" /> Delete
        </button>
      </div>
    </div>
  );
}

// ── Item card (worksheet project or saved list) ─────────────────────────
const AI_STUDIO_ROUTES = {
  "packaging":    "/ai/packaging-designer",
  "logo":         "/ai/logo-designer",
  "meta-ads":     "/ai/meta-ads-manager",
  "reddit-ads":   "/ai/reddit-ads-manager",
  "ads-designer": "/ai/ads-designer",
  "formulation":  "/ai/formulation-creator",
};

function ItemCard({ item, dbNames, onDelete, onMove, onPreview, selected, onToggleSelect, blueprintLive }) {
  // CPG Agreements draft wrapper — a saved template draft. Same pink
  // badge as the intake wrapper so founders see one consistent "CPG
  // Agreements" surface in Projects. Opens the TemplateView with the
  // saved values pre-filled via `?project=<wrapperId>`.
  if (item._kind === "agreements-draft") {
    const openHref = `/agreements/template/${encodeURIComponent(item.agreements_doc_id)}?project=${encodeURIComponent(item.id)}`;
    const savedAt = item.agreements_saved_at || item.created_at;
    return (
      <div
        className="surface p-6 flex flex-col relative min-w-0 overflow-hidden"
        data-testid={`project-card-${item.id}`}
      >
        {onToggleSelect && (
          <label
            className="absolute top-3 left-3 z-10 grid place-items-center w-6 h-6 rounded bg-white/90 backdrop-blur border border-shell-border cursor-pointer hover:border-primary"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={!!selected}
              onChange={onToggleSelect}
              data-testid={`project-select-${item.id}`}
              className="w-3.5 h-3.5 accent-primary cursor-pointer"
            />
          </label>
        )}
        <span
          className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full text-[9px] font-bold uppercase tracking-widest px-2 py-1 bg-pink-100 text-pink-700"
          data-testid="project-agreements-badge"
        >
          CPG Agreements
        </span>
        <div className="mt-6 min-w-0">
          <h3 className="text-lg font-bold text-neutral-900 truncate">{item.name}</h3>
          <p className="text-xs text-neutral-500 mt-1">
            Agreement Draft
            {savedAt ? ` · Saved ${new Date(savedAt).toLocaleDateString()}` : ""}
          </p>
        </div>
        <div className="mt-6 flex items-center gap-3 flex-wrap">
          <Link
            to={openHref}
            className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1 whitespace-nowrap"
            data-testid={`project-agreements-draft-open-${item.id}`}
          >
            Open <ChevronRight className="w-3 h-3" />
          </Link>
          {onMove && (
            <button
              onClick={onMove}
              data-testid={`project-move-${item.id}`}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <MoveRight className="w-3 h-3" /> Move
            </button>
          )}
          {onDelete && (
            <button
              onClick={onDelete}
              data-testid={`project-delete-${item.id}`}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive"
            >
              <Trash2 className="w-3 h-3" /> Delete
            </button>
          )}
        </div>
      </div>
    );
  }

  // CPG Agreements intake — rendered as its own compact card so
  // multiple submissions saved to the same project each get their
  // own tile with the founder's brand name front-and-center.
  if (item._kind === "agreements-intake") {
    return (
      <div
        className="surface p-6 flex flex-col relative min-w-0 overflow-hidden"
        data-testid={`project-card-${item.id}`}
      >
        {onToggleSelect && (
          <label
            className="absolute top-3 left-3 z-10 grid place-items-center w-6 h-6 rounded bg-white/90 backdrop-blur border border-shell-border cursor-pointer hover:border-primary"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={!!selected}
              onChange={onToggleSelect}
              data-testid={`project-select-${item.id}`}
              className="w-3.5 h-3.5 accent-primary cursor-pointer"
            />
          </label>
        )}
        <span
          className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full text-[9px] font-bold uppercase tracking-widest px-2 py-1 bg-pink-100 text-pink-700"
          data-testid="project-agreements-badge"
        >
          CPG Agreements
        </span>
        <div className="mt-6 min-w-0">
          <h3 className="text-lg font-bold text-neutral-900 truncate">{item.brand_name}</h3>
          <p className="text-xs text-neutral-500 mt-1">
            Project &amp; Brand Intake · Submitted
            {item.attached_at ? ` · ${new Date(item.attached_at).toLocaleDateString()}` : ""}
          </p>
          {item._owningProjectName && !item._owningIsWrapper && (
            <p className="text-[11px] text-neutral-500 mt-2">In {item._owningProjectName}</p>
          )}
          {item.counts?.total ? (
            <p className="text-xs text-neutral-600 mt-2">{item.counts.total} document{item.counts.total === 1 ? "" : "s"} in plan</p>
          ) : null}
        </div>
        <div className="mt-6 flex items-center gap-3 flex-wrap">
          <Link
            to={`/agreements/plan?project=${encodeURIComponent(item.intakeProjectId)}`}
            className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1 whitespace-nowrap"
            data-testid={`project-agreements-open-${item.intakeProjectId}`}
          >
            Open <ChevronRight className="w-3 h-3" />
          </Link>
          {onMove && (
            <button
              onClick={onMove}
              data-testid={`project-move-${item.id}`}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <MoveRight className="w-3 h-3" /> Move
            </button>
          )}
          {onDelete && (
            <button
              onClick={onDelete}
              data-testid={`project-delete-${item.id}`}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive"
            >
              <Trash2 className="w-3 h-3" /> Delete
            </button>
          )}
        </div>
      </div>
    );
  }

  const isWorksheet = item._kind === "project" && item.type === "worksheet" && item.worksheet_id;
  const isList = item._kind === "list";
  const isAiStudio = item._kind === "project" && item.type === "ai_studio";
  const isBlueprint = item._kind === "project" && item.type === "launch_blueprint";
  const bpVariant   = item.blueprint_variant || "submit";
  const isBpDraft   = isBlueprint && bpVariant === "draft";
  const isBpSubmit  = isBlueprint && bpVariant === "submit";
  // Financial Projection variants — mirrors the blueprint pattern.
  const isFinancial = item._kind === "project" && item.type === "financial_projection";
  const finVariant  = item.financial_variant || "draft";
  const isFinDraft  = isFinancial && finVariant === "draft";
  const isFinResult = isFinancial && finVariant === "result";
  // AI Brand Profile snapshots — reuse the AI Studio visual family so
  // founders instantly recognise them as an AI CPG Tools artifact.
  const isBrandProfile = item._kind === "project" && item.type === "brand_profile";
  const bpUnderReview = isBlueprint && blueprintLive?.status === "submitted";
  // Only apply the live status/score to the submit-variant snapshot
  // that MATCHES the current live blueprint. Every submit-variant
  // stores its own `blueprint_submitted_at` at save time — if it
  // doesn't line up with `blueprintLive.submitted_at` this snapshot
  // is a historical submission and its status/score belongs to a
  // review that has already been superseded.
  const bpMatchesLive = isBpSubmit
    && blueprintLive?.submitted_at
    && item.blueprint_submitted_at === blueprintLive.submitted_at;
  const bpScore   = bpMatchesLive
    ? (blueprintLive?.score ?? blueprintLive?.panel_score ?? blueprintLive?.review_score ?? null)
    : null;
  const bpScored  = bpMatchesLive && (bpScore != null || blueprintLive?.status === "reviewed");
  const bpItemUnderReview = bpMatchesLive && bpUnderReview;
  const isPlain = item._kind === "project" && !item.type;
  const aiRoute = isAiStudio ? (AI_STUDIO_ROUTES[item.ai_studio_tool] || "/ai-center") : null;
  const isFormulation = isAiStudio && item.ai_studio_tool === "formulation";

  return (
    <div className="surface p-6 flex flex-col relative min-w-0 overflow-hidden" data-testid={`project-card-${item.id}`}>
      {onToggleSelect && (
        <label
          className="absolute top-3 left-3 z-10 grid place-items-center w-6 h-6 rounded bg-white/90 backdrop-blur border border-shell-border cursor-pointer hover:border-primary"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            checked={!!selected}
            onChange={onToggleSelect}
            data-testid={`project-select-${item.id}`}
            className="w-3.5 h-3.5 accent-primary cursor-pointer"
          />
        </label>
      )}
      {isAiStudio && item.ai_studio_thumb_url ? (
        <img
          src={`${BACKEND_URL}${item.ai_studio_thumb_url}`}
          alt={item.name}
          className="mb-4 -mx-6 -mt-6 block w-[calc(100%+3rem)] h-40 object-cover rounded-t-xl bg-muted"
          data-testid={`project-card-thumb-${item.id}`}
        />
      ) : null}
      {isBlueprint && (
        <div className="absolute top-3 right-3 flex flex-col items-end gap-1">
          <span
            className={`inline-flex items-center gap-1 rounded-full text-[9px] font-bold uppercase tracking-widest px-2 py-1 ${isBpDraft ? "bg-pink-100 text-pink-700" : "bg-primary/10 text-primary"}`}
            data-testid="project-blueprint-badge"
          >
            <Award className="w-3 h-3" />
            {isBpDraft ? "Draft" : "Submitted · read-only"}
          </span>
          {bpScored && (
            <span
              className="inline-flex items-center gap-1 rounded-full text-[10px] font-bold px-2 py-1 bg-emerald-100 text-emerald-700"
              data-testid="project-blueprint-score"
            >
              Score {bpScore ?? "—"}
            </span>
          )}
          {bpItemUnderReview && !bpScored && (
            <span className="inline-flex items-center gap-1 rounded-full text-[10px] font-semibold px-2 py-1 bg-slate-100 text-slate-600">
              Under review
            </span>
          )}
        </div>
      )}
      {isFinancial && (
        <div className="absolute top-3 right-3 flex flex-col items-end gap-1">
          <span
            className={`inline-flex items-center gap-1 rounded-full text-[9px] font-bold uppercase tracking-widest px-2 py-1 ${isFinDraft ? "bg-sky-100 text-sky-700" : "bg-emerald-100 text-emerald-700"}`}
            data-testid="project-financial-badge"
          >
            <TrendingUp className="w-3 h-3" />
            {isFinDraft ? "Draft" : "Result · read-only"}
          </span>
        </div>
      )}
      {isBrandProfile && (
        <div className="absolute top-3 right-3 flex flex-col items-end gap-1">
          <span
            className="inline-flex items-center gap-1 rounded-full text-[9px] font-bold uppercase tracking-widest px-2 py-1 bg-violet-100 text-violet-700"
            data-testid="project-brand-profile-badge"
          >
            <Sparkles className="w-3 h-3" /> AI Brand Profile
          </span>
        </div>
      )}
      {isWorksheet ? (
        <FileText className="w-5 h-5 text-primary" />
      ) : isList ? (
        <TableIcon className="w-5 h-5 text-shell-side" />
      ) : isAiStudio ? (
        <Sparkles className="w-5 h-5 text-primary" />
      ) : isBlueprint ? (
        <Award className="w-5 h-5 text-primary" />
      ) : isFinancial ? (
        <TrendingUp className="w-5 h-5 text-primary" />
      ) : isBrandProfile ? (
        <Sparkles className="w-5 h-5 text-violet-600" />
      ) : (
        <FolderOpen className="w-5 h-5 text-shell-accent" />
      )}
      <p className="mt-4 font-display text-xl break-words">{item.name}</p>
      {isBlueprint && (
        <p className={`text-[10px] uppercase tracking-widest mt-1 font-semibold ${isBpDraft ? "text-pink-700" : "text-primary/80"}`}>
          {isBpDraft
            ? "Founder Launch Blueprint — draft snapshot"
            : "Founder Launch Blueprint — submitted"}
        </p>
      )}
      {isFinancial && (
        <p className={`text-[10px] uppercase tracking-widest mt-1 font-semibold ${isFinDraft ? "text-sky-700" : "text-emerald-700"}`}>
          {isFinDraft
            ? `${financialToolLabel(item.financial_kind)} — draft snapshot`
            : `${financialToolLabel(item.financial_kind)} — saved result`}
        </p>
      )}
      {isBrandProfile && (
        <p className="text-[10px] uppercase tracking-widest mt-1 font-semibold text-violet-700">
          AI Brand Profile — saved snapshot
        </p>
      )}
      {isWorksheet && item.worksheet_title && item.worksheet_title !== item.name && (
        <p className="text-xs text-muted-foreground mt-1 truncate" title={item.worksheet_title}>
          Worksheet · {item.worksheet_title}
        </p>
      )}
      {isWorksheet && item.module_slug && (
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1 font-semibold break-words">
          Module · {item.module_slug}
        </p>
      )}
      {isList && (
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1 font-semibold break-words">
          Saved list · {dbNames[item.database_slug] || item.database_slug} · {(item.row_ids || []).length} records
        </p>
      )}
      {isAiStudio && (
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1 font-semibold break-words">
          AI Studio · {(item.ai_studio_tool || "").replace(/-/g, " ")} · {item.ai_studio_image_count || 0} image{(item.ai_studio_image_count || 0) === 1 ? "" : "s"}
        </p>
      )}
      {item.event_date && (
        <p className="text-xs text-muted-foreground mt-2 inline-flex items-center gap-1">
          <CalendarDays className="w-3 h-3" /> {new Date(item.event_date).toLocaleDateString()}
        </p>
      )}
      {item.comments && (
        <p className="text-xs text-muted-foreground mt-2 line-clamp-3">{item.comments}</p>
      )}
      <p className="text-xs text-muted-foreground mt-2">Created {new Date(item.created_at).toLocaleDateString()}</p>
      <div className="mt-6 flex items-center gap-3 flex-wrap">
        {isBpDraft ? (
          bpUnderReview ? (
            <div
              className="w-full rounded-lg border border-pink-200 bg-pink-50 px-3 py-2 text-[11px] text-pink-900 leading-relaxed"
              data-testid={`project-blueprint-draft-waiting-${item.id}`}
            >
              Once the submitted data is reviewed and you receive a score, you can submit this draft. Until then, please wait.
            </div>
          ) : (
            <Link
              to={`/blueprint?snapshot=${item.id}`}
              data-testid={`project-open-blueprint-${item.id}`}
              className="btn-primary text-xs inline-flex items-center gap-1"
            >
              <ExternalLink className="w-3 h-3" /> Open
            </Link>
          )
        ) : isBpSubmit ? (
          <Link
            to={`/blueprint?snapshot=${item.id}`}
            data-testid={`project-open-blueprint-${item.id}`}
            className="btn-primary text-xs inline-flex items-center gap-1"
          >
            <ExternalLink className="w-3 h-3" /> Open
          </Link>
        ) : isFinancial ? (
          <Link
            to={`/tools/${item.financial_kind || "sales-estimator"}?snapshot=${item.id}`}
            data-testid={`project-open-financial-${item.id}`}
            className="btn-primary text-xs inline-flex items-center gap-1"
          >
            <ExternalLink className="w-3 h-3" /> Open
          </Link>
        ) : isBrandProfile ? (
          <Link
            to={`/ai-cpg-tools/brand-profile?snapshot=${item.id}`}
            data-testid={`project-open-brand-profile-${item.id}`}
            className="btn-primary text-xs inline-flex items-center gap-1"
          >
            <ExternalLink className="w-3 h-3" /> Open
          </Link>
        ) : isList ? (
          // Saved lists now open on their own routed page so founders
          // see every column + a Full-record accordion inside the
          // AppShell (with sidebar) rather than the compact popup.
          <Link
            to={`/projects/lists/${item.id}`}
            data-testid={`project-open-list-page-${item.id}`}
            className="btn-primary text-xs inline-flex items-center gap-1"
          >
            <ExternalLink className="w-3 h-3" /> Open
          </Link>
        ) : isFormulation ? (
          <Link
            to={`/projects/formulations/${item.id}`}
            data-testid={`project-open-formulation-${item.id}`}
            className="btn-primary text-xs inline-flex items-center gap-1"
          >
            <ExternalLink className="w-3 h-3" /> Open
          </Link>
        ) : onPreview && (
          <button
            onClick={onPreview}
            data-testid={`project-open-preview-${item.id}`}
            className="btn-primary text-xs inline-flex items-center gap-1"
          >
            <ExternalLink className="w-3 h-3" /> Open
          </button>
        )}
        {isWorksheet && (
          <a
            href={`${API}/worksheets/${item.worksheet_id}/file`}
            target="_blank"
            rel="noreferrer"
            data-testid={`project-open-worksheet-${item.id}`}
            className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
          >
            <ExternalLink className="w-3 h-3" /> Open in new tab
          </a>
        )}
        {isList && (
          // "Full database" jumps to the source database on the DATA tab
          // (not the overview), highlighting the exact row when the list
          // contains only one record. Deep-link support lives in
          // DatabaseDetail (`?highlight=` / `?tab=`).
          <Link
            to={
              (item.row_ids || []).length === 1
                ? `/databases/${item.database_slug}?highlight=${encodeURIComponent(item.row_ids[0])}`
                : `/databases/${item.database_slug}?tab=data`
            }
            data-testid={`project-open-list-${item.id}`}
            className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
          >
            <ExternalLink className="w-3 h-3" /> Full database
          </Link>
        )}
        {isAiStudio && !isFormulation && (
          <Link
            to={aiRoute}
            data-testid={`project-open-aistudio-${item.id}`}
            className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
          >
            <ExternalLink className="w-3 h-3" /> In AI Studio
          </Link>
        )}
        <button
          onClick={onMove}
          data-testid={`project-move-${item.id}`}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <MoveRight className="w-3 h-3" /> Move
        </button>
        <button onClick={onDelete} data-testid={`project-delete-${item.id}`}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive">
          <Trash2 className="w-3 h-3" /> Delete
        </button>
      </div>
    </div>
  );
}

// ── Items list view (compact table) ─────────────────────────────────────
function ItemsListView({ items, dbNames, onDelete, onMove, onPreview, selected, onToggleSelect, blueprintLive }) {
  const isRowSelected = (it) => selected && selected.has(`${it._kind}:${it.id}`);
  // ── Newest first + client-side pagination ────────────────────────
  // Founders wanted the most recent work at the top of the list so
  // they don't have to scroll past week-old drafts to reach today's.
  // We sort a shallow copy (never mutate the caller's array) by the
  // best available "recency" timestamp — attached_at for CPG
  // Agreements intake rows, financial_saved_at / blueprint_submitted_at
  // for save/submit snapshots, otherwise created_at — and paginate
  // 25 per page. Both live in this component so the sort/paging state
  // is scoped to the list view and doesn't leak to card view.
  const PAGE_SIZE = 25;
  const [page, setPage] = useState(1);
  const sortedItems = useMemo(() => {
    const recency = (it) => (
      it?.attached_at
      || it?.financial_saved_at
      || it?.blueprint_submitted_at
      || it?.updated_at
      || it?.created_at
      || ""
    );
    return [...items].sort((a, b) => (recency(b) || "").localeCompare(recency(a) || ""));
  }, [items]);
  const totalPages = Math.max(1, Math.ceil(sortedItems.length / PAGE_SIZE));
  // Clamp the current page whenever the item count shrinks (e.g. after
  // a delete on the last page) so we don't render an empty table.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);
  const pageStart = (page - 1) * PAGE_SIZE;
  const pageEnd = pageStart + PAGE_SIZE;
  const pagedItems = sortedItems.slice(pageStart, pageEnd);
  return (
    <div className="mt-4" data-testid="projects-items-list-wrap">
    <div className="rounded-xl border border-shell-border bg-white overflow-x-auto" data-testid="projects-items-list">
      <table className="w-full text-sm min-w-[640px]">
        <thead className="bg-muted/60 text-xs uppercase tracking-widest text-muted-foreground">
          <tr>
            {onToggleSelect && <th className="w-10 px-2 py-2"></th>}
            <th className="text-left px-4 py-2 font-semibold min-w-[240px]">Name</th>
            <th className="text-left px-4 py-2 font-semibold whitespace-nowrap">Type</th>
            <th className="text-left px-4 py-2 font-semibold whitespace-nowrap">Date</th>
            <th className="text-left px-4 py-2 font-semibold whitespace-nowrap">Created</th>
            <th className="px-4 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {pagedItems.map((it) => {
            // CPG Agreements saved draft row — mirrors the intake row
            // visually (pink CPG Agreements badge, same test-ids), but
            // Open navigates to the TemplateView with the saved values
            // pre-filled via `?project=<wrapperId>` so the founder can
            // continue editing where they left off.
            if (it._kind === "agreements-draft") {
              const openHref = `/agreements/template/${encodeURIComponent(it.agreements_doc_id)}?project=${encodeURIComponent(it.id)}`;
              const savedAt = it.agreements_saved_at || it.created_at;
              return (
                <tr key={it.id} className="border-t border-shell-border hover:bg-muted/30 transition" data-testid={`project-row-${it.id}`}>
                  {onToggleSelect && (
                    <td className="px-2 py-3">
                      <label className="grid place-items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isRowSelected(it)}
                          onChange={() => onToggleSelect(it)}
                          data-testid={`project-select-${it.id}`}
                          className="w-3.5 h-3.5 accent-primary cursor-pointer"
                        />
                      </label>
                    </td>
                  )}
                  <td className="px-4 py-3 font-semibold text-neutral-900">
                    <div className="truncate">{it.name}</div>
                    <div className="text-[11px] text-neutral-500 font-normal mt-0.5">Agreement Draft</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 rounded-full text-[10px] font-bold uppercase tracking-widest px-2 py-1 bg-pink-100 text-pink-700">
                      CPG Agreements
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                    {savedAt ? new Date(savedAt).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                    {savedAt ? new Date(savedAt).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3 justify-end">
                      <Link
                        to={openHref}
                        className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1"
                        data-testid={`project-agreements-draft-open-${it.id}`}
                      >
                        Open <ChevronRight className="w-3 h-3" />
                      </Link>
                      {onMove && (
                        <button
                          onClick={() => onMove(it)}
                          data-testid={`project-move-${it.id}`}
                          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                        >
                          <MoveRight className="w-3 h-3" /> Move
                        </button>
                      )}
                      {onDelete && (
                        <button
                          onClick={() => onDelete(it)}
                          data-testid={`project-delete-${it.id}`}
                          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            }
            // CPG Agreements intake row — full parity with regular
            // project rows now: checkbox for bulk selection, Move
            // button (targets the underlying wrapper project so the
            // whole CPG Agreements card moves as a unit), Open, and
            // Delete.
            if (it._kind === "agreements-intake") {
              return (
                <tr key={it.id} className="border-t border-shell-border hover:bg-muted/30 transition" data-testid={`project-row-${it.id}`}>
                  {onToggleSelect && (
                    <td className="px-2 py-3">
                      <label className="grid place-items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isRowSelected(it)}
                          onChange={() => onToggleSelect(it)}
                          data-testid={`project-select-${it.id}`}
                          className="w-3.5 h-3.5 accent-primary cursor-pointer"
                        />
                      </label>
                    </td>
                  )}
                  <td className="px-4 py-3 font-semibold text-neutral-900">
                    <div className="truncate">{it.brand_name}</div>
                    {it._owningProjectName && !it._owningIsWrapper && (
                      <div className="text-[11px] text-neutral-500 font-normal mt-0.5">In {it._owningProjectName}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 rounded-full text-[10px] font-bold uppercase tracking-widest px-2 py-1 bg-pink-100 text-pink-700">
                      CPG Agreements
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                    {it.attached_at ? new Date(it.attached_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                    {it.attached_at ? new Date(it.attached_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3 justify-end">
                      <Link
                        to={`/agreements/plan?project=${encodeURIComponent(it.intakeProjectId)}`}
                        className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1"
                        data-testid={`project-agreements-open-${it.intakeProjectId}`}
                      >
                        Open <ChevronRight className="w-3 h-3" />
                      </Link>
                      {onMove && (
                        <button
                          onClick={() => onMove(it)}
                          data-testid={`project-move-${it.id}`}
                          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                        >
                          <MoveRight className="w-3 h-3" /> Move
                        </button>
                      )}
                      {onDelete && (
                        <button
                          onClick={() => onDelete(it)}
                          data-testid={`project-delete-${it.id}`}
                          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            }
            const isWorksheet = it._kind === "project" && it.type === "worksheet" && it.worksheet_id;
            const isList = it._kind === "list";
            const isAiStudio = it._kind === "project" && it.type === "ai_studio";
            const isBlueprint = it._kind === "project" && it.type === "launch_blueprint";
            const bpVariant   = it.blueprint_variant || "submit";
            const isBpDraft   = isBlueprint && bpVariant === "draft";
            const isBpSubmit  = isBlueprint && bpVariant === "submit";
            const isFinancial = it._kind === "project" && it.type === "financial_projection";
            const finVariant  = it.financial_variant || "draft";
            const isFinDraft  = isFinancial && finVariant === "draft";
            const isFinResult = isFinancial && finVariant === "result";
            const isBrandProfile = it._kind === "project" && it.type === "brand_profile";
            const bpUnderReview = isBlueprint && blueprintLive?.status === "submitted";
            // Same per-item isolation as ItemCard — status/score only
            // applies to the submit snapshot whose submitted_at pairs
            // with the current live blueprint.
            const bpMatchesLive = isBpSubmit
              && blueprintLive?.submitted_at
              && it.blueprint_submitted_at === blueprintLive.submitted_at;
            const bpScore   = bpMatchesLive
              ? (blueprintLive?.score ?? blueprintLive?.panel_score ?? blueprintLive?.review_score ?? null)
              : null;
            const bpScored  = bpMatchesLive && (bpScore != null || blueprintLive?.status === "reviewed");
            const bpItemUnderReview = bpMatchesLive && bpUnderReview;
            const aiRoute = isAiStudio ? (AI_STUDIO_ROUTES[it.ai_studio_tool] || "/ai-center") : null;
            const isFormulation = isAiStudio && it.ai_studio_tool === "formulation";
            const typeLabel = isWorksheet
              ? "Worksheet"
              : isList
              ? "Saved list"
              : isAiStudio
              ? "AI Studio"
              : isBpDraft
              ? "Launch Blueprint · Draft"
              : isBpSubmit
              ? "Launch Blueprint · Submitted"
              : isFinDraft
              ? `${financialToolLabel(it.financial_kind)} · Draft`
              : isFinResult
              ? `${financialToolLabel(it.financial_kind)} · Result`
              : isBrandProfile
              ? "AI Brand Profile · Saved"
              : "Project";
            return (
              <tr key={`${it._kind}-${it.id}`} className="border-t border-shell-border hover:bg-muted/30"
                  data-testid={`projects-item-row-${it.id}`}>
                {onToggleSelect && (
                  <td className="w-10 px-2 py-3 text-center">
                    <input
                      type="checkbox"
                      checked={isRowSelected(it)}
                      onChange={() => onToggleSelect(it)}
                      data-testid={`project-select-row-${it.id}`}
                      className="accent-primary cursor-pointer"
                    />
                  </td>
                )}
                <td className="px-4 py-3">
                  <div className="flex items-start gap-2 min-w-0">
                    {isWorksheet ? <FileText className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                      : isList ? <TableIcon className="w-4 h-4 text-shell-side shrink-0 mt-0.5" />
                      : isAiStudio ? <Sparkles className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                      : isBlueprint ? <Award className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                      : isFinancial ? <TrendingUp className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                      : isBrandProfile ? <Sparkles className="w-4 h-4 text-violet-600 shrink-0 mt-0.5" />
                      : <FolderOpen className="w-4 h-4 text-shell-accent shrink-0 mt-0.5" />}
                    <span className="font-semibold break-words min-w-0">{it.name}</span>
                    {isBlueprint && (
                      <span
                        className={`inline-flex items-center gap-1 rounded-full text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 shrink-0 ${isBpDraft ? "bg-pink-100 text-pink-700" : "bg-primary/10 text-primary"}`}
                        data-testid={`project-blueprint-badge-list-${it.id}`}
                      >
                        {isBpDraft ? "Draft" : "Submitted · read-only"}
                      </span>
                    )}
                    {isFinancial && (
                      <span
                        className={`inline-flex items-center gap-1 rounded-full text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 shrink-0 ${isFinDraft ? "bg-sky-100 text-sky-700" : "bg-emerald-100 text-emerald-700"}`}
                        data-testid={`project-financial-badge-list-${it.id}`}
                      >
                        {isFinDraft ? "Draft" : "Result · read-only"}
                      </span>
                    )}
                    {bpScored && (
                      <span
                        className="inline-flex items-center gap-1 rounded-full text-[9px] font-bold px-2 py-0.5 bg-emerald-100 text-emerald-700 shrink-0"
                        data-testid={`project-blueprint-score-list-${it.id}`}
                      >
                        Score {bpScore ?? "—"}
                      </span>
                    )}
                    {bpItemUnderReview && !bpScored && (
                      <span className="inline-flex items-center gap-1 rounded-full text-[9px] font-semibold px-2 py-0.5 bg-slate-100 text-slate-600 shrink-0">
                        Under review
                      </span>
                    )}
                  </div>
                  {(isWorksheet && it.module_slug) && (
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5 break-words">
                      Module · {it.module_slug}
                    </p>
                  )}
                  {isList && (
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5 break-words">
                      {dbNames[it.database_slug] || it.database_slug} · {(it.row_ids || []).length} records
                    </p>
                  )}
                  {isAiStudio && (
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5 break-words">
                      {(it.ai_studio_tool || "").replace(/-/g, " ")} · {it.ai_studio_image_count || 0} image{(it.ai_studio_image_count || 0) === 1 ? "" : "s"}
                    </p>
                  )}
                </td>
                <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{typeLabel}</td>
                <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                  {it.event_date ? new Date(it.event_date).toLocaleDateString() : "—"}
                </td>
                <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                  {new Date(it.created_at).toLocaleDateString()}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {isBpDraft ? (
                    bpUnderReview ? (
                      <span
                        className="inline-block text-[10px] font-semibold text-pink-800 bg-pink-50 border border-pink-200 rounded px-2 py-1 max-w-[240px] whitespace-normal leading-snug"
                        data-testid={`project-blueprint-draft-waiting-list-${it.id}`}
                      >
                        Reviewed score pending — draft cannot be submitted yet.
                      </span>
                    ) : (
                      <Link
                        to={`/blueprint?snapshot=${it.id}`}
                        data-testid={`project-open-blueprint-${it.id}`}
                        className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1 mr-3"
                      >
                        <ExternalLink className="w-3 h-3" /> Open
                      </Link>
                    )
                  ) : isBpSubmit ? (
                    <Link
                      to={`/blueprint?snapshot=${it.id}`}
                      data-testid={`project-open-blueprint-${it.id}`}
                      className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1 mr-3"
                    >
                      <ExternalLink className="w-3 h-3" /> Open
                    </Link>
                  ) : isFinancial ? (
                    <Link
                      to={`/tools/${it.financial_kind || "sales-estimator"}?snapshot=${it.id}`}
                      data-testid={`project-open-financial-${it.id}`}
                      className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1 mr-3"
                    >
                      <ExternalLink className="w-3 h-3" /> Open
                    </Link>
                  ) : isBrandProfile ? (
                    <Link
                      to={`/ai-cpg-tools/brand-profile?snapshot=${it.id}`}
                      data-testid={`project-open-brand-profile-${it.id}`}
                      className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1 mr-3"
                    >
                      <ExternalLink className="w-3 h-3" /> Open
                    </Link>
                  ) : isList ? (
                    // Saved lists route to their dedicated page — same
                    // rationale as the card view: full sidebar shell +
                    // per-row Full-record accordion.
                    <Link
                      to={`/projects/lists/${it.id}`}
                      data-testid={`project-open-list-page-${it.id}`}
                      className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1 mr-3"
                    >
                      <ExternalLink className="w-3 h-3" /> Open
                    </Link>
                  ) : isFormulation ? (
                    <Link
                      to={`/projects/formulations/${it.id}`}
                      data-testid={`project-open-formulation-${it.id}`}
                      className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1 mr-3"
                    >
                      <ExternalLink className="w-3 h-3" /> Open
                    </Link>
                  ) : onPreview && (
                    <button
                      onClick={() => onPreview(it)}
                      data-testid={`project-open-preview-${it.id}`}
                      className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1 mr-3"
                    >
                      <ExternalLink className="w-3 h-3" /> Open
                    </button>
                  )}
                  {isWorksheet && (
                    <a
                      href={`${API}/worksheets/${it.worksheet_id}/file`}
                      target="_blank" rel="noreferrer"
                      data-testid={`project-open-worksheet-${it.id}`}
                      className="text-xs font-semibold text-muted-foreground hover:underline inline-flex items-center gap-1 mr-3"
                    >
                      <ExternalLink className="w-3 h-3" /> New tab
                    </a>
                  )}
                  {isList && (
                    <Link to={`/databases/${it.database_slug}`}
                      data-testid={`project-open-list-${it.id}`}
                      className="text-xs font-semibold text-muted-foreground hover:underline inline-flex items-center gap-1 mr-3"
                    >
                      <ExternalLink className="w-3 h-3" /> Full database
                    </Link>
                  )}
                  {isAiStudio && !isFormulation && (
                    <Link to={aiRoute}
                      data-testid={`project-open-aistudio-${it.id}`}
                      className="text-xs font-semibold text-muted-foreground hover:underline inline-flex items-center gap-1 mr-3"
                    >
                      <ExternalLink className="w-3 h-3" /> In AI Studio
                    </Link>
                  )}
                  <button onClick={() => onMove(it)} data-testid={`project-move-${it.id}`}
                    className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mr-3"
                  >
                    <MoveRight className="w-3 h-3" /> Move
                  </button>
                  <button onClick={() => onDelete(it)} data-testid={`project-delete-${it.id}`}
                    className="text-xs text-muted-foreground hover:text-destructive inline-flex items-center gap-1">
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    {totalPages > 1 && (
      <div
        className="mt-3 flex items-center justify-between gap-2 text-xs text-muted-foreground"
        data-testid="projects-items-list-pagination"
      >
        <p>
          Showing <span className="font-semibold text-foreground">{pageStart + 1}</span>
          –<span className="font-semibold text-foreground">{Math.min(pageEnd, sortedItems.length)}</span>
          {" "}of{" "}
          <span className="font-semibold text-foreground">{sortedItems.length}</span>
        </p>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            data-testid="projects-items-list-prev"
            className="inline-flex items-center gap-1 rounded-full border border-shell-border px-3 py-1.5 font-semibold hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> Prev
          </button>
          <span
            className="px-2 font-semibold text-foreground"
            data-testid="projects-items-list-page-indicator"
          >
            {page} / {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            data-testid="projects-items-list-next"
            className="inline-flex items-center gap-1 rounded-full border border-shell-border px-3 py-1.5 font-semibold hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    )}
    </div>
  );
}

// ── Move-to-folder modal ────────────────────────────────────────────────
function MoveToFolderModal({ movingItem, folders, onClose, onSelect }) {
  return (
    <div className="fixed inset-0 z-50 bg-shell-side/60 backdrop-blur-sm grid place-items-center p-4"
         data-testid="projects-move-modal">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-shell-border">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Move</p>
          <h3 className="font-display text-2xl mt-1 truncate">{movingItem.name}</h3>
          <p className="text-xs text-muted-foreground mt-1">Pick a destination folder or move it back to the root.</p>
        </div>
        <div className="max-h-[60vh] overflow-auto">
          <button
            onClick={() => onSelect("")}
            data-testid="projects-move-target-root"
            className={"w-full text-left px-6 py-3 border-b border-shell-border hover:bg-muted/30 inline-flex items-center gap-2 " +
              (movingItem.currentParent ? "" : "bg-muted/40")}
          >
            <ChevronLeft className="w-4 h-4 text-muted-foreground" />
            <span className="font-semibold">Root (no folder)</span>
          </button>
          {folders.map((f) => (
            <button
              key={f.id}
              onClick={() => onSelect(f.id)}
              data-testid={`projects-move-target-${f.id}`}
              className={"w-full text-left px-6 py-3 border-b border-shell-border hover:bg-muted/30 inline-flex items-center gap-2 " +
                (movingItem.currentParent === f.id ? "bg-muted/40" : "")}
            >
              <Folder className="w-4 h-4 text-shell-accent" />
              <span className="font-semibold">{f.name}</span>
            </button>
          ))}
        </div>
        <div className="px-6 py-4 border-t border-shell-border flex items-center justify-end">
          <button
            onClick={onClose}
            data-testid="projects-move-cancel"
            className="text-xs font-semibold text-muted-foreground hover:text-foreground border border-shell-border rounded-full px-4 py-2"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
